import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validator from 'validator';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ButtonMain from '../../components/common/ButtonMain';
import FooterTrustChat from '../../components/common/FooterTrustChat';
import PasswordField from '../../components/common/passwordInput';
import Navbar from '../../components/common/Navbar';
import { resetUser } from '../../store/features/loginUser';
import { resetPasswordApi } from '../../store/features/resetPassword';
import { BASE_URL } from '../../api/ApiConfig';
import { createAppSocket } from '../../api/createAppSocket';

const ENDPOINT = `${BASE_URL}`;

// ✅ Password policy message
const PASSWORD_REQUIREMENTS =
  "Password must include 1 special character, 1 uppercase, 1 lowercase, minimum 9 characters.";

function CreatePassword() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const socketRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const currentLanguage = useSelector((state) => state.language.currentLanguage);
  const t = useSelector((state) => state.language.translations[currentLanguage]);

  const [stateName, setStateMethod] = useState({
    firstPassword: '',
    firstPasswordInvalidError: '', // string
    firstPasswordInvalid: false,   // boolean
    secondPassword: '',
    secondPasswordInvalidError: '', // string
    secondPasswordInvalid: false,   // boolean
    FormSubmitting: false,
    firstPasswordState: false,
    secondPasswordState: false,
    showPassword: false,
  });

  // extract token/email
  const urlSearchParams = new URLSearchParams(window.location.search);
  const idToken = urlSearchParams.get('q');

  const getEmailFromToken = (token) => {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const parsedPayload = JSON.parse(decodedPayload);
      return parsedPayload.email || '';
    } catch {
      return '';
    }
  };
  const email = getEmailFromToken(idToken);

  useEffect(() => {
    socketRef.current = createAppSocket();

    const handleForceLogout = (data) => {
      if (data?.email && data.email === email) {
        try {
          localStorage.removeItem("token");
          dispatch(resetUser());
        } finally {
          window.location.href = "/login";
        }
      }
    };

    socketRef.current.on("reset-logout", handleForceLogout);

    return () => {
      if (socketRef.current) {
        socketRef.current.off("reset-logout", handleForceLogout);
        socketRef.current.disconnect();
      }
    };
  }, [email, dispatch]);

  const ValidatePassword = (password) => {
    return validator.isStrongPassword(password, {
      minLength: 8,
      minLowercase: 1,
      minUppercase: 1,
      minNumbers: 1,
      minSymbols: 1,
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (!value) {
      if (name === 'firstPassword') {
        setStateMethod((s) => ({
          ...s,
          firstPassword: '',
          firstPasswordInvalidError: '',
          firstPasswordInvalid: false,
          firstPasswordState: false,
        }));
      } else if (name === 'secondPassword') {
        setStateMethod((s) => ({
          ...s,
          secondPassword: '',
          secondPasswordInvalidError: '',
          secondPasswordInvalid: false,
          secondPasswordState: false,
        }));
      }
      return;
    }

    if (name === 'firstPassword') {
      setStateMethod((s) => ({
        ...s,
        firstPassword: value,
        firstPasswordInvalidError: '',  // clear on typing
        firstPasswordInvalid: false,    // don’t show red
        firstPasswordState: false,      // don’t show green yet

        // clear confirm state if it existed
        ...(s.secondPassword
          ? {
            secondPasswordInvalidError: '',
            secondPasswordInvalid: false,
            secondPasswordState: false,
          }
          : {}),
      }));
    } else if (name === 'secondPassword') {
      setStateMethod((s) => ({
        ...s,
        secondPassword: value,
        secondPasswordInvalidError: '', // clear on typing
        secondPasswordInvalid: false,
        secondPasswordState: false,
      }));
    } else {
      setStateMethod((s) => ({ ...s, [name]: value }));
    }
  };


  const ResetPasswordHandler = async () => {
    setFormSubmitted(true);

    let hasError = false;
    let updates = { ...stateName };

    // ✅ Show "Password is required" if empty, otherwise show password requirements
    if (!stateName.firstPassword) {
      updates.firstPasswordInvalidError = t.ENTR_PASS_PH;
      updates.firstPasswordInvalid = true;
      updates.firstPasswordState = false;
      hasError = true;
    } else if (!ValidatePassword(stateName.firstPassword)) {
      updates.firstPasswordInvalidError = PASSWORD_REQUIREMENTS;
      updates.firstPasswordInvalid = true;
      updates.firstPasswordState = false;
      hasError = true;
    } else {
      updates.firstPasswordInvalidError = '';
      updates.firstPasswordInvalid = false;
      updates.firstPasswordState = true;
    }

    // ✅ Show "Re-enter password" if empty, otherwise show confirmation error
    if (!stateName.secondPassword) {
      updates.secondPasswordInvalidError = t.REENTR_PASS_PH;
      updates.secondPasswordInvalid = true;
      updates.secondPasswordState = false;
      hasError = true;
    } else if (stateName.secondPassword !== stateName.firstPassword) {
      updates.secondPasswordInvalidError = t.CONFIRMPASSWORDERROR;
      updates.secondPasswordInvalid = true;
      updates.secondPasswordState = false;
      hasError = true;
    } else {
      updates.secondPasswordInvalidError = '';
      updates.secondPasswordInvalid = false;
      updates.secondPasswordState = true;
    }

    setStateMethod(updates);

    if (hasError) return;

    const resetPassword = dispatch(
      resetPasswordApi({
        UserData: {
          password: stateName.firstPassword,
          confirmPassword: stateName.secondPassword,
          token: idToken,
        },
      }),
    );

    resetPassword
      .then(async (data) => {
        if (!data?.payload) {
          toast.error('Some Error Occurred. Please try again later.');
        } else if (data?.payload?.status === 200) {
          if (socketRef.current?.connected) {
            socketRef.current.emit("user-logout", { email });
          }
          toast.success(data?.payload?.message);
          dispatch(resetUser());
          navigate('/login', { replace: true });
        } else {
          toast.error(data.payload?.message || 'Some Error Occurred. Please try again later.');
        }
      })
      .catch((error) => {
        toast.error(error?.toString() || 'Unexpected error');
      });
  };

  return (
    <>
      <Navbar />
      <div className="Forget-main-container">
        <div className="inputDivStyle-forget">
          <div className="textDiv">
            <p className="text__create">{t.CREATE_NEW_PASS}</p>
            <p className="text__start">{t.CREATE_PASS_TEXT}</p>
          </div>
          <div>
            <PasswordField
              name="firstPassword"
              id="firstPassword"
              type="password"
              title={t.ENTR_PWD}
              placeholder={t.ENTR_PASS_PH}
              value={stateName.firstPassword}
              onChange={handleChange}
              error={stateName.firstPasswordInvalidError}
              className={clsx(
                stateName.firstPasswordInvalid && 'input__error',
                stateName.firstPasswordState && 'input__success',
              )}
            />
            <br />
            <PasswordField
              name="secondPassword"
              id="secondPassword"
              type="password"
              title={t.ENTR_PWD_C}
              placeholder={t.REENTR_PASS_PH}
              value={stateName.secondPassword}
              onChange={handleChange}
              error={stateName.secondPasswordInvalidError}
              className={clsx(
                stateName.secondPasswordInvalid && 'input__error',
                stateName.secondPasswordState && 'input__success',
              )}
            />
          </div>
          <div className="btn-class">
            <ButtonMain
              onClick={ResetPasswordHandler}
              title={t.BTN_CONTINUE}
              className="buttonStyleEnable"
            />
          </div>
        </div>
      </div>
      <FooterTrustChat />
    </>
  );
}

export default CreatePassword;